import { useState, useEffect, useCallback } from "react";
import { useApi } from "./useApi";
import { apiRequest, ApiError } from "src/services/apiClient";

export interface FinApiAccount {
    id: number;
    accountName: string;
    iban: string | null;
    accountType: string;
    balance: number;
    currency: string;
}

/** readableId + patient name for a matched invoice — enough to show "→ MS-0001 · Max
 *  Mustermann" next to a matched transaction instead of just a bare "Matched" chip. */
export interface FinApiMatchedInvoiceInfo {
    id: string;
    readableId: string;
    patientName?: string;
}

export interface FinApiTransaction {
    id: string;
    amount: number;
    currency: string;
    purpose: string | null;
    counterpartName: string | null;
    bookingDate: string;
    matchedInvoiceIds: string[];
    matchedInvoices: FinApiMatchedInvoiceInfo[];
    matchedBy: "auto" | "manual" | null;
    ignored: boolean;
    /** Set when this unmatched transaction's purpose references an invoice that's already
     *  fully paid — a likely duplicate payment. The doctor is auto-alerted by email once;
     *  the patient is only notified when the doctor explicitly confirms via notifyPatientDuplicate. */
    possibleDuplicateInvoice?: { id: string; readableId: string; patientNotifiedAt?: string | null } | null;
    /** Which doctor's connected account this transaction came from — getStatus now merges
     *  every doctor's account together (see banksApiController.ts), so every item in the feed
     *  needs to say whose it is. Passed back as `doctorId` on match/unmatch/ignore/unignore so
     *  the write lands on the right connection instead of the acting user's own. */
    ownerDoctorId: string;
    ownerDoctorName: string | null;
}

/** An invoice matched to 2+ distinct bank transactions — a stronger signal than an
 *  amount-based "overpaid" check, since it flags the double-recording itself rather than
 *  inferring it from the total (see TodoCard.tsx's doublePayments item). */
export interface FinApiDoubleMatchedInvoice {
    id: string;
    readableId: string;
    patientName?: string;
    matchCount: number;
    totalMatched: number;
    ownerDoctorId: string;
    ownerDoctorName: string | null;
}

export interface FinApiPendingTransaction {
    id: string;
    amount: number;
    currency: string;
    purpose: string | null;
    counterpartName: string | null;
    valueDate: string; // expected settlement date
    ownerDoctorId: string;
    ownerDoctorName: string | null;
}

/** One doctor's connected account, as getStatus's `connections` array describes it — used to
 *  render a per-doctor summary (connected/not, last synced) alongside the merged transaction
 *  feed below, since several doctors in the same praxis can each have their own connection. */
export interface FinApiConnection {
    doctorId: string;
    doctorName: string | null;
    bankName: string | null;
    accounts: FinApiAccount[];
    lastSyncAt: string | null;
}

export interface FinApiStatus {
    connected: boolean;
    connections?: FinApiConnection[];
    accounts?: FinApiAccount[];
    bankName?: string;
    recentTransactions?: FinApiTransaction[];
    pendingTransactions?: FinApiPendingTransaction[];
    doubleMatchedInvoices?: FinApiDoubleMatchedInvoice[];
    lastSyncAt?: string;
}

export function useFinApi(refreshTrigger = 0) {
    const [trigger, setTrigger] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [syncError, setSyncError] = useState<string | null>(null);
    const [connecting, setConnecting] = useState(false);
    const [connectError, setConnectError] = useState<{ code: "LOCKED" | "GENERIC"; status?: number; message?: string } | null>(null);
    // Always bypass cache — bank status/transactions are live data
    const { data, loading, refetch } = useApi<FinApiStatus>("/banksapi/status", trigger + refreshTrigger + 1);

    const refresh = useCallback(() => setTrigger(t => t + 1), []);

    // No auto-sync on mount: /banksapi/sync is a live bank refresh (refresh=true + Customer-IP),
    // which the bank treats as customer-present access and gates behind a TAN. Status reads
    // BANKSapi's cached data, which it refreshes in the background up to 4x/day without SCA;
    // a live refresh only happens when the user explicitly clicks sync.

    const connect = useCallback(async (): Promise<string | null> => {
        if (connecting) return null;
        setConnecting(true);
        setConnectError(null);
        try {
            // Best-effort: delete any stale BANKSapi session before starting a new one.
            // Only do this when we *actually* have an existing session/connection to clear.
            const hadSession = !!data?.connected || !!localStorage.getItem("banksapi_pending");
            if (hadSession) {
                try { await apiRequest("/banksapi/session", { method: "DELETE" }); } catch { /* ignore */ }
            }

            const res = await apiRequest<{ formUrl: string }>("/banksapi/connect", { method: "POST" });
            // Mark that we're mid-connection so the callback fires on return even without URL params
            localStorage.setItem("banksapi_pending", "1");
            return res.formUrl;
        } catch (err: any) {
            if (err instanceof ApiError && err.status === 423) {
                setConnectError({ code: "LOCKED", status: 423, message: err.message });
            } else if (err instanceof ApiError) {
                setConnectError({ code: "GENERIC", status: err.status, message: err.message });
            } else {
                setConnectError({ code: "GENERIC", message: typeof err?.message === "string" ? err.message : undefined });
            }
            return null;
        } finally {
            setConnecting(false);
        }
    }, [connecting, data?.connected]);

    const finaliseCallback = useCallback(async (bankAccessId?: string): Promise<FinApiCallbackResult> => {
        try {
            const res = await apiRequest<{ duplicateWarning?: { doctorNames: string[] } }>("/banksapi/callback", {
                method: "POST",
                body: JSON.stringify({ bankAccessId }),
            }, { "Content-Type": "application/json" });
            localStorage.removeItem("banksapi_pending");
            refresh();
            return { ok: true, duplicateWarning: res?.duplicateWarning };
        } catch {
            localStorage.removeItem("banksapi_pending");
            return { ok: false };
        }
    }, [refresh]);

    const sync = useCallback(async () => {
        setSyncing(true);
        setSyncError(null);
        try {
            await apiRequest("/banksapi/sync", { method: "POST" });
        } catch (err: any) {
            setSyncError(err?.message ?? "Sync failed");
        } finally {
            setSyncing(false);
            refresh();
        }
    }, [refresh]);

    const disconnect = useCallback(async () => {
        try { await apiRequest("/banksapi/disconnect", { method: "DELETE" }); } catch { /* ignore */ }
        refresh();
    }, [refresh]);

    // doctorId — the transaction's own ownerDoctorId (see FinApiTransaction) — tells the backend
    // which connection this write belongs to, now that the feed merges every doctor's account
    // together; omit it (or pass the caller's own) for a single-connection praxis, unchanged.
    const manualMatch = useCallback(async (transactionId: string, allocations: { invoiceId: string; amount: number }[], doctorId?: string) => {
        await apiRequest(
            `/banksapi/transactions/${encodeURIComponent(transactionId)}/match`,
            { method: "POST", body: JSON.stringify({ allocations, ...(doctorId && { doctorId }) }) },
            { "Content-Type": "application/json" },
        );
    }, []);

    const unmatch = useCallback(async (transactionId: string, doctorId?: string) => {
        await apiRequest(
            `/banksapi/transactions/${encodeURIComponent(transactionId)}/match${doctorId ? `?doctorId=${encodeURIComponent(doctorId)}` : ""}`,
            { method: "DELETE" },
        );
    }, []);

    const markIgnored = useCallback(async (transactionId: string, doctorId?: string) => {
        await apiRequest(
            `/banksapi/transactions/${encodeURIComponent(transactionId)}/ignore`,
            { method: "PATCH", body: JSON.stringify({ ...(doctorId && { doctorId }) }) },
            { "Content-Type": "application/json" },
        );
    }, []);

    const unignore = useCallback(async (transactionId: string, doctorId?: string) => {
        await apiRequest(
            `/banksapi/transactions/${encodeURIComponent(transactionId)}/ignore${doctorId ? `?doctorId=${encodeURIComponent(doctorId)}` : ""}`,
            { method: "DELETE" },
        );
    }, []);

    const notifyPatientDuplicate = useCallback(async (transactionId: string, invoiceId: string, doctorId?: string) => {
        await apiRequest(
            "/banksapi/duplicate-alerts/notify-patient",
            { method: "POST", body: JSON.stringify({ transactionId, invoiceId, ...(doctorId && { doctorId }) }) },
            { "Content-Type": "application/json" },
        );
    }, []);

    const sortByDateDesc = <T extends { bookingDate?: string; valueDate?: string }>(txs: T[]) =>
        [...txs].sort((a, b) => {
            const da = a.bookingDate ?? a.valueDate ?? "";
            const db = b.bookingDate ?? b.valueDate ?? "";
            return db.localeCompare(da);
        });

    return {
        loading,
        connected:           data?.connected           ?? false,
        connections:         data?.connections         ?? [],
        accounts:            data?.accounts            ?? [],
        bankName:            data?.bankName            ?? null,
        recentTransactions:         sortByDateDesc(data?.recentTransactions         ?? []),
        pendingTransactions:        sortByDateDesc(data?.pendingTransactions        ?? []),
        lastSyncAt:          data?.lastSyncAt,
        syncing,
        syncError,
        connecting,
        connectError,
        refresh,
        connect,
        finaliseCallback,
        sync,
        disconnect,
        manualMatch,
        unmatch,
        markIgnored,
        unignore,
        notifyPatientDuplicate,
        refetch,
    };
}

// ── Callback handler hook (used on Dashboard mount) ───────────────────────────

export interface FinApiCallbackResult {
    ok: boolean;
    /** The new connection's IBAN(s) are already connected under a different doctor in this
     *  praxis — not blocked (could be a legitimate shared account), just surfaced so whoever
     *  connected it can go disconnect the stray one themselves. */
    duplicateWarning?: { doctorNames: string[] };
}

export function useFinApiCallback(
    finaliseCallback: (bankAccessId?: string) => Promise<FinApiCallbackResult>,
    onDuplicateWarning?: (doctorNames: string[]) => void,
) {
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        // BANKSapi may return the bankAccessId under various param names
        const id = params.get("id") ?? params.get("bankAccessId") ?? params.get("bankzugangId") ?? undefined;
        const pending = !!localStorage.getItem("banksapi_pending");

        if (!id && !pending) return;

        // Strip any BANKSapi params from the URL
        ["id", "bankAccessId", "bankzugangId", "code"].forEach((k) => params.delete(k));
        const newSearch = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));

        finaliseCallback(id).then((result) => {
            if (result.duplicateWarning) onDuplicateWarning?.(result.duplicateWarning.doctorNames);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
